import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import { 
    Video, ArrowLeft, CheckCircle, Clock, AlertCircle, Trophy,
    Star, TrendingUp, Target, Activity, Eye, BarChart3, Download,
    Loader2, Share2, Play, Pause, SkipForward, Scissors,
    MessageSquare, User, FileText, Film, Sparkles
} from 'lucide-react';
import { videoAnalysisApi, highlightsApi, reportsApi } from '../lib/api';
import { toast } from 'sonner';

const VideoAnalysisDetailPage = () => {
    const { videoId } = useParams();
    const navigate = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');
    const [highlights, setHighlights] = useState([]);
    const [generatingHighlight, setGeneratingHighlight] = useState(false);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        loadAnalysis();
    }, [videoId]);

    useEffect(() => {
        if (analysis && ['pending', 'processing'].includes(analysis.analysis_status)) {
            setPolling(true);
            const interval = setInterval(loadAnalysis, 5000);
            return () => clearInterval(interval);
        } else {
            setPolling(false);
        }
    }, [analysis?.analysis_status]);

    const loadAnalysis = async () => {
        try {
            const response = await videoAnalysisApi.getResult(videoId);
            setAnalysis(response.data);
            
            // Load highlights
            try {
                const hlRes = await highlightsApi.getForVideo(videoId);
                setHighlights(hlRes.data.highlights || []);
            } catch (e) {}
        } catch (error) {
            console.error('Error loading analysis:', error);
            toast.error('Failed to load analysis');
        } finally {
            setLoading(false);
        }
    };

    const seekToMoment = (timestamp) => {
        if (videoRef.current) {
            videoRef.current.currentTime = timestamp;
            videoRef.current.play();
            setVideoPlaying(true);
        }
    };

    const handleGenerateHighlight = async (format = '16:9') => {
        setGeneratingHighlight(true);
        try {
            await highlightsApi.generate({
                video_id: videoId,
                format: format,
                include_intro: true
            });
            toast.success('Highlight reel generation started! This takes 1-3 minutes.');
            setTimeout(loadAnalysis, 5000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate highlight');
        } finally {
            setGeneratingHighlight(false);
        }
    };

    const handleDownloadPdf = () => {
        const url = reportsApi.generatePdf(videoId);
        window.open(url, '_blank');
    };

    const getRadarData = () => {
        if (!analysis?.metrics) return [];
        return Object.entries(analysis.metrics).map(([key, value]) => ({
            metric: key.length > 12 ? key.substring(0, 12) + '...' : key,
            fullName: key,
            value: value,
            fullMark: 100
        }));
    };

    const MetricBar = ({ label, score }) => (
        <div className="space-y-1">
            <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium">{score}/100</span>
            </div>
            <div className="h-2 bg-[#0a1628] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${
                    score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : score >= 40 ? 'bg-orange-500' : 'bg-red-500'
                }`} style={{ width: `${score}%` }} />
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] text-white flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!analysis) {
        return (
            <div className="min-h-screen bg-[#0a1628] text-white flex flex-col items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                <p>Analysis not found</p>
                <Link to="/video-analysis"><Button className="mt-4">Back to Video Analysis</Button></Link>
            </div>
        );
    }

    const isCompleted = analysis.analysis_status === 'completed';
    const keyMoments = analysis.key_moments || [];

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                    <div className="flex items-center justify-between h-14 md:h-16">
                        <div className="flex items-center gap-2">
                            <Link to="/video-analysis" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-5 h-5" />
                                <span className="hidden md:inline">Back</span>
                            </Link>
                            <span className="text-muted-foreground">/</span>
                            <span className="font-medium truncate max-w-[200px]">{analysis.athlete_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {polling && (
                                <span className="flex items-center gap-1 text-xs text-yellow-400">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Analyzing...
                                </span>
                            )}
                            {isCompleted && (
                                <Button variant="outline" size="sm" onClick={handleDownloadPdf} className="border-white/10" data-testid="download-pdf-btn">
                                    <Download className="w-4 h-4 mr-1" /> PDF Report
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
                {/* Status Banner */}
                {!isCompleted && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                        analysis.analysis_status === 'processing' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                        analysis.analysis_status === 'pending' ? 'bg-blue-500/10 border border-blue-500/20' :
                        'bg-red-500/10 border border-red-500/20'
                    }`}>
                        {analysis.analysis_status === 'processing' ? (
                            <><Loader2 className="w-5 h-5 text-yellow-400 animate-spin" /><div>
                                <p className="font-medium text-yellow-400">AI Analysis in Progress</p>
                                <p className="text-sm text-muted-foreground">This usually takes 2-5 minutes. Page will auto-refresh.</p>
                            </div></>
                        ) : analysis.analysis_status === 'pending' ? (
                            <><Clock className="w-5 h-5 text-blue-400" /><div>
                                <p className="font-medium text-blue-400">Analysis Pending</p>
                                <p className="text-sm text-muted-foreground">Your video is in the queue.</p>
                            </div></>
                        ) : (
                            <><AlertCircle className="w-5 h-5 text-red-400" /><div>
                                <p className="font-medium text-red-400">Analysis Failed</p>
                                <p className="text-sm text-muted-foreground">{analysis.error_message || 'Please try uploading again.'}</p>
                            </div></>
                        )}
                    </div>
                )}

                {/* Athlete Info + Video Player */}
                <div className="grid gap-6 lg:grid-cols-2 mb-6">
                    {/* Video Player */}
                    <Card className="bg-[#0d2847] border-white/5">
                        <CardContent className="p-4">
                            <video
                                ref={videoRef}
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/video-analysis/video/${videoId}`}
                                className="w-full rounded-lg bg-black aspect-video"
                                controls
                                onPlay={() => setVideoPlaying(true)}
                                onPause={() => setVideoPlaying(false)}
                                data-testid="video-player"
                            />
                            <div className="mt-3 flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <div>
                                    <p className="font-medium">{analysis.athlete_name}</p>
                                    <p className="text-xs text-muted-foreground">{analysis.sport} {analysis.position ? `| ${analysis.position}` : ''}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Score + Radar */}
                    {isCompleted && (
                        <Card className="bg-[#0d2847] border-white/5">
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-primary" />
                                    Performance Radar
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-center mb-2">
                                    <div className="text-center">
                                        <p className="text-4xl font-bold" style={{ color: analysis.overall_score >= 80 ? '#22c55e' : analysis.overall_score >= 60 ? '#eab308' : '#ef4444' }}>
                                            {analysis.overall_score}
                                        </p>
                                        <p className="text-xs text-muted-foreground">Overall Score</p>
                                        {analysis.potential_rating && (
                                            <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                                                analysis.potential_rating === 'Elite Prospect' ? 'bg-purple-500/20 text-purple-400' :
                                                analysis.potential_rating === 'High Potential' ? 'bg-green-500/20 text-green-400' :
                                                'bg-blue-500/20 text-blue-400'
                                            }`}>{analysis.potential_rating}</span>
                                        )}
                                    </div>
                                </div>
                                {analysis.metrics && (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <RadarChart data={getRadarData()}>
                                            <PolarGrid stroke="rgba(255,255,255,0.1)" />
                                            <PolarAngleAxis dataKey="metric" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 9 }} />
                                            <Radar name="Performance" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} strokeWidth={2} />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>

                {isCompleted && (
                    <>
                        {/* Section Tabs */}
                        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                            {[
                                { id: 'overview', label: 'Metrics', icon: BarChart3 },
                                { id: 'moments', label: `Key Moments (${keyMoments.length})`, icon: Clock },
                                { id: 'highlights', label: 'Highlights', icon: Film },
                                { id: 'analysis', label: 'Full Analysis', icon: MessageSquare },
                            ].map(tab => (
                                <Button key={tab.id} size="sm"
                                    variant={activeSection === tab.id ? 'default' : 'outline'}
                                    onClick={() => setActiveSection(tab.id)}
                                    className={activeSection !== tab.id ? 'border-white/10' : ''}
                                    data-testid={`section-tab-${tab.id}`}
                                >
                                    <tab.icon className="w-4 h-4 mr-1" />{tab.label}
                                </Button>
                            ))}
                            <Link to={`/video-analysis/${videoId}/biomechanics`}>
                                <Button size="sm" variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="biomechanics-link">
                                    <Activity className="w-4 h-4 mr-1" />Body Tracking
                                </Button>
                            </Link>
                        </div>

                        {/* Metrics Section */}
                        {activeSection === 'overview' && (
                            <div className="grid gap-6 lg:grid-cols-3">
                                {/* Detailed Metrics */}
                                <Card className="bg-[#0d2847] border-white/5 lg:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />Performance Breakdown</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        {analysis.metrics && Object.entries(analysis.metrics).map(([key, value]) => (
                                            <MetricBar key={key} label={key} score={value} />
                                        ))}
                                    </CardContent>
                                </Card>

                                {/* Strengths + Weaknesses */}
                                <div className="space-y-4">
                                    <Card className="bg-[#0d2847] border-white/5">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="flex items-center gap-2 text-green-400 text-sm"><TrendingUp className="w-4 h-4" />Strengths</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ul className="space-y-2">
                                                {analysis.strengths?.map((s, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                                        <span className="text-sm">{s}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                    <Card className="bg-[#0d2847] border-white/5">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="flex items-center gap-2 text-yellow-400 text-sm"><Target className="w-4 h-4" />Improve</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ul className="space-y-2">
                                                {analysis.areas_for_improvement?.map((a, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                                        <span className="text-sm">{a}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        )}

                        {/* Key Moments Section */}
                        {activeSection === 'moments' && (
                            <Card className="bg-[#0d2847] border-white/5">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-cyan-400" />
                                        Key Moments (Timestamped)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {keyMoments.length === 0 ? (
                                        <p className="text-center text-muted-foreground py-8">
                                            No key moments detected. Try re-analyzing with a longer video.
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {keyMoments.map((moment, i) => {
                                                const mins = Math.floor(moment.timestamp / 60);
                                                const secs = Math.floor(moment.timestamp % 60);
                                                const ratingColors = {
                                                    'Excellent': 'text-green-400 bg-green-500/10',
                                                    'Good': 'text-blue-400 bg-blue-500/10',
                                                    'Average': 'text-yellow-400 bg-yellow-500/10',
                                                    'Poor': 'text-red-400 bg-red-500/10',
                                                };
                                                return (
                                                    <div key={i} className="flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#0a1628]/80 cursor-pointer transition-colors"
                                                        onClick={() => seekToMoment(moment.timestamp)}
                                                        data-testid={`moment-${i}`}>
                                                        <Button variant="ghost" size="sm" className="w-10 h-10 rounded-full bg-primary/20 hover:bg-primary/30">
                                                            <Play className="w-4 h-4 text-primary" />
                                                        </Button>
                                                        <div className="w-16 text-center">
                                                            <span className="text-sm font-mono font-bold text-primary">{mins}:{secs.toString().padStart(2, '0')}</span>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{moment.description}</p>
                                                            {moment.category && <p className="text-xs text-muted-foreground">{moment.category}</p>}
                                                        </div>
                                                        {moment.rating && (
                                                            <span className={`px-2 py-1 text-xs rounded-full ${ratingColors[moment.rating] || ratingColors['Average']}`}>
                                                                {moment.rating}
                                                            </span>
                                                        )}
                                                        <span className="text-xs text-muted-foreground">{moment.duration}s</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Highlights Section */}
                        {activeSection === 'highlights' && (
                            <div className="space-y-4">
                                <Card className="bg-[#0d2847] border-white/5">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Film className="w-5 h-5 text-primary" />
                                            Auto Highlight Reels
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {keyMoments.length === 0 ? (
                                            <p className="text-center text-muted-foreground py-6">
                                                No key moments available to create highlights. Ensure analysis detected timestamped moments.
                                            </p>
                                        ) : (
                                            <div className="space-y-4">
                                                <p className="text-sm text-muted-foreground">
                                                    Generate auto-cut highlight reels from {keyMoments.length} key moments using FFmpeg.
                                                </p>
                                                <div className="flex flex-wrap gap-3">
                                                    <Button onClick={() => handleGenerateHighlight('16:9')} disabled={generatingHighlight} data-testid="generate-highlight-landscape">
                                                        {generatingHighlight ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}
                                                        Generate 16:9 (YouTube/Desktop)
                                                    </Button>
                                                    <Button onClick={() => handleGenerateHighlight('9:16')} disabled={generatingHighlight} variant="outline" className="border-white/10" data-testid="generate-highlight-portrait">
                                                        {generatingHighlight ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scissors className="w-4 h-4 mr-2" />}
                                                        Generate 9:16 (TikTok/Reels)
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Existing highlights */}
                                        {highlights.length > 0 && (
                                            <div className="mt-6 space-y-3">
                                                <h4 className="font-medium text-sm">Generated Highlights</h4>
                                                {highlights.map((hl, i) => (
                                                    <div key={i} className="flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg" data-testid={`highlight-item-${i}`}>
                                                        <Film className="w-5 h-5 text-primary" />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium">Highlight Reel ({hl.format})</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {hl.clip_count} clips | {hl.duration?.toFixed(1)}s | {hl.status}
                                                            </p>
                                                        </div>
                                                        {hl.status === 'ready' && (
                                                            <div className="flex gap-2">
                                                                <a href={highlightsApi.stream(hl.highlight_id)} target="_blank" rel="noreferrer">
                                                                    <Button size="sm" variant="outline" className="border-white/10">
                                                                        <Play className="w-3 h-3 mr-1" /> Watch
                                                                    </Button>
                                                                </a>
                                                                <Button size="sm" variant="outline" className="border-white/10"
                                                                    onClick={async () => { await highlightsApi.publish(hl.highlight_id); toast.success('Highlight published!'); loadAnalysis(); }}>
                                                                    <Share2 className="w-3 h-3 mr-1" /> Share
                                                                </Button>
                                                            </div>
                                                        )}
                                                        {hl.status === 'processing' && (
                                                            <span className="text-xs text-yellow-400 flex items-center gap-1">
                                                                <Loader2 className="w-3 h-3 animate-spin" /> Processing
                                                            </span>
                                                        )}
                                                        {hl.coach_review && (
                                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                                hl.coach_review.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                                                            }`}>
                                                                {hl.coach_review.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Full Analysis Section */}
                        {activeSection === 'analysis' && (
                            <div className="grid gap-6 lg:grid-cols-2">
                                <Card className="bg-[#0d2847] border-white/5 lg:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" />Detailed AI Analysis</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{analysis.detailed_analysis}</p>
                                    </CardContent>
                                </Card>
                                {analysis.comparison_data && (
                                    <Card className="bg-[#0d2847] border-white/5">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-primary" />Comparison</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-3">
                                            {analysis.comparison_data.level_comparison && (
                                                <div><p className="text-xs text-muted-foreground mb-1">vs. Peers</p><p className="text-sm">{analysis.comparison_data.level_comparison}</p></div>
                                            )}
                                            {analysis.comparison_data.style_comparison && (
                                                <div><p className="text-xs text-muted-foreground mb-1">Playing Style</p><p className="text-sm">{analysis.comparison_data.style_comparison}</p></div>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                                {analysis.scout_notes && (
                                    <Card className="bg-[#0d2847] border-white/5">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-purple-400" />Scout Notes</CardTitle>
                                        </CardHeader>
                                        <CardContent><p className="text-sm">{analysis.scout_notes}</p></CardContent>
                                    </Card>
                                )}
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default VideoAnalysisDetailPage;
